import { useEffect } from 'react';
import { Transaction } from './types';
import * as sentry from '@ardoq/sentry';

export type Span = {
  sentrySpan: any;
};

/**
 * See documentation for `startTransaction`.
 */
export const startSpan = (transaction: Transaction, description: string) => ({
  sentrySpan: sentry.startSpan(transaction.sentryTransaction, description),
});

/**
 * See documentation for `endTransaction`.
 */
export const endSpan = (span: Span) => sentry.endSpan(span.sentrySpan);

export const useSpan = (transaction: Transaction, description: string) => {
  const span = startSpan(transaction, description);
  useEffect(() => {
    endSpan(span);
  });
};

/**
 * See documentation for `functionWithTransaction`.
 */
export const functionWithSpan = <F extends (...args: any[]) => any>(
  transaction: Transaction,
  description: string,
  functionToBeMeasured: F
): ((...args: Parameters<F>) => ReturnType<F>) => {
  const functionWithMeasurement = (...parameters: Parameters<F>) => {
    const span = startSpan(transaction, description);
    const result = functionToBeMeasured(...parameters);
    endSpan(span);
    return result;
  };
  return functionWithMeasurement;
};

/**
 * See documentation for `functionWithTransactionAsync`.
 */
export const functionWithSpanAsync = <
  F extends (...args: any[]) => Promise<any>,
>(
  transaction: Transaction,
  description: string,
  functionToBeMeasured: F
): ((...args: Parameters<F>) => Promise<Awaited<ReturnType<F>>>) => {
  const functionWithMeasurement = async (...parameters: Parameters<F>) => {
    const span = startSpan(transaction, description);
    const result = await functionToBeMeasured(...parameters);
    endSpan(span);
    return result;
  };
  return functionWithMeasurement;
};

/**
 * See documentation for `runWithTransaction`.
 */
export const runWithSpan = <F extends () => any>(
  transaction: Transaction,
  description: string,
  functionToBeMeasured: F
): ReturnType<F> => {
  const span = startSpan(transaction, description);
  const result = functionToBeMeasured();
  endSpan(span);
  return result;
};

/**
 * See documentation for `runWithTransactionAsync`.
 */
export const runWithSpanAsync = async <F extends () => Promise<any>>(
  transaction: Transaction,
  description: string,
  functionToBeMeasured: F
): Promise<Awaited<ReturnType<F>>> => {
  const span = startSpan(transaction, description);
  const result = await functionToBeMeasured();
  endSpan(span);
  return result;
};
